/**
 * Trust & usage policy — the "default-use + hedge + acquiesce" model
 * (decision-log 2026-06-20, Codex-verified). REPLACES the confirm gate with three
 * SEPARATE axes so a single `>= HIGH` comparison can never punch through the safety
 * line:
 *
 *   evidenceKind     — how a proposal earned trust (deterministic | explicit |
 *                      cross_model | self | acquiescence).
 *   conversationUse  — how confidently the agent may USE it in dialogue
 *                      (low | medium | high) — a HEDGE, not a fact verdict.
 *   guardEligible    — may a RISK guard (pre-commit/deploy failure warning) read it?
 *                      TRUE only for deterministic/explicit. acquiescence/self/
 *                      cross_model are NEVER guard-eligible.
 *
 * Pure (no store, no I/O, clock passed in) so every rule is unit-testable in
 * isolation — same shape as `tier.ts`. The store calls these to derive the stored
 * `conversation_use` / `guard_eligible` columns and the read-time effective use.
 */

import type { SourceKind } from "./types.js";

/** How a proposal earned its trust. Superset of the older `VerifiedBy`. */
export type EvidenceKind = "deterministic" | "explicit" | "cross_model" | "self" | "acquiescence";

/** Dialogue confidence hedge (NOT graph membership, NOT a truth verdict). */
export type ConversationUse = "low" | "medium" | "high";

/**
 * What KIND of fact this is — gates the acquiescence cap and time decay.
 * `objective` = an external, checkable truth (a version, an API shape): acquiescence
 * alone can't make it HIGH. The rest are about THIS user (preference/habit) or a
 * mutable world (state/plan/relationship/environment) — those a user's repeated
 * acquiescence legitimately raises, and those decay with age.
 */
export type FactType =
  | "objective"
  | "preference"
  | "habit"
  | "project_context"
  | "state"
  | "plan"
  | "relationship"
  | "environment";

/** Acquiescence strength — did the user merely not object, or build further on it? */
export type AcquiescenceStrength = "weak" | "strong";

/**
 * Score deltas (decision-log 2026-06-20). A single accumulating score drives the
 * conversationUse threshold; verification kinds seed it, acquiescence nudges it,
 * a correction slams it negative.
 */
export const SCORE = {
  self: 1,
  cross_model: 2,
  acq_weak: 0.5,
  acq_strong: 1.5,
  correction: -5,
} as const;

/** score >= HIGH_AT -> high; >= MEDIUM_AT -> medium; else low. */
export const HIGH_AT = 3;
export const MEDIUM_AT = 1.5;

/**
 * Verification sentinel for deterministic/explicit evidence: well above HIGH so a
 * verified proposal is HIGH on its own, and a single correction (-5) still drops it
 * below MEDIUM (entrenchment can't survive one explicit correction).
 */
export const VERIFIED_SCORE = 5;

/** ONLY these evidence kinds may ever be guard-eligible. */
const GUARD_KINDS: ReadonlySet<EvidenceKind> = new Set<EvidenceKind>(["deterministic", "explicit"]);

/** Fact types whose confidence decays with age. Objective facts / stable habits don't. */
const DECAY_PRONE: ReadonlySet<FactType> = new Set<FactType>([
  "preference",
  "state",
  "plan",
  "relationship",
  "environment",
]);

/** A decay-prone fact older than this (days) drops one confidence notch at read time. */
export const DECAY_DAYS = 90;

const USE_RANK: Readonly<Record<ConversationUse, number>> = { low: 1, medium: 2, high: 3 };

/** Bridge the older 3-value `verifiedBy` onto the 5-value `evidenceKind`. */
export function evidenceKindFromVerifiedBy(v: "deterministic" | "cross-model" | "self"): EvidenceKind {
  return v === "deterministic" ? "deterministic" : v === "cross-model" ? "cross_model" : "self";
}

/** Seed score from the evidence kind a proposal was created with. */
export function initialScore(evidenceKind: EvidenceKind): number {
  switch (evidenceKind) {
    case "deterministic":
    case "explicit":
      return VERIFIED_SCORE;
    case "cross_model":
      return SCORE.cross_model;
    case "self":
      return SCORE.self;
    case "acquiescence":
      return 0; // earns its score only through recordAcquiescence
  }
}

/**
 * Derive how confidently the agent may USE a proposal in conversation. The heart of
 * the hedge model — and where the safety caps live.
 */
export function deriveConversationUse(p: {
  readonly score: number;
  readonly evidenceKind: EvidenceKind;
  readonly factType?: FactType | null;
  readonly contested?: boolean;
  readonly sourceKind?: SourceKind;
}): ConversationUse {
  // A contested fact (a user correction stands against it) is never asserted
  // confidently, whatever its score.
  if (p.contested) return "low";

  // Verified evidence (deterministic / explicit) is authoritative on its own — EXCEPT
  // when the proposal is conversation-sourced: a "matching" deterministic record may be
  // a summary derived FROM this very conversation (circular), so a conversation-sourced
  // proposal is capped at MEDIUM even with deterministic evidence (composes the Phase-2
  // ceiling with the verified branch — Codex 2026-06-20).
  if (p.evidenceKind === "deterministic" || p.evidenceKind === "explicit") {
    return p.sourceKind === "conversation" ? "medium" : "high";
  }

  let use: ConversationUse = p.score >= HIGH_AT ? "high" : p.score >= MEDIUM_AT ? "medium" : "low";

  // Fact-type cap: an OBJECTIVE truth can't reach HIGH on self/acquiescence alone —
  // those are "this user is comfortable with it", not verification of the world.
  // (cross_model IS an independent check, so it may.)
  if (
    use === "high" &&
    p.factType === "objective" &&
    (p.evidenceKind === "self" || p.evidenceKind === "acquiescence")
  ) {
    use = "medium";
  }

  // Phase-2 conversation ceiling: an auto conversation-sourced proposal never reaches
  // HIGH on its own (a "matching" record may be a summary derived FROM this very
  // conversation — circular). HIGH stays human-confirm / deterministic.
  if (use === "high" && p.sourceKind === "conversation") use = "medium";

  return use;
}

/**
 * May a RISK guard read this proposal? TRUE only for verified, uncontested,
 * non-conversation evidence. This is the core safety line: a wrong acquiesced fact is
 * cheap in dialogue (and correctable) but expensive in a guard — so the guard reads
 * a strictly narrower set than conversation does.
 */
export function deriveGuardEligible(p: {
  readonly evidenceKind: EvidenceKind;
  readonly contested?: boolean;
  readonly sourceKind?: SourceKind;
}): boolean {
  if (p.contested) return false;
  if (!GUARD_KINDS.has(p.evidenceKind)) return false; // self / cross_model / acquiescence: never
  // Even "explicit" from a RAW transcript isn't auto-trusted for a guard — it needs
  // human confirm or independent deterministic corroboration first.
  if (p.sourceKind === "conversation") return false;
  return true;
}

/**
 * Read-time time decay: a stale decay-prone fact drops one confidence notch. Applied
 * at READ, never mutated into storage, so it's deterministic and needs no background
 * job. `lastAtISO` is the most recent touch (last acquiescence, else proposal time).
 */
export function applyDecay(
  use: ConversationUse,
  factType: FactType | null | undefined,
  lastAtISO: string | null | undefined,
  nowISO: string,
): ConversationUse {
  if (use === "low") return use;
  if (!factType || !DECAY_PRONE.has(factType)) return use;
  if (!lastAtISO) return use;
  const last = Date.parse(lastAtISO);
  const now = Date.parse(nowISO);
  if (!Number.isFinite(last) || !Number.isFinite(now)) return use;
  const ageDays = (now - last) / 86_400_000;
  if (ageDays < DECAY_DAYS) return use;
  return use === "high" ? "medium" : "low";
}

/**
 * Derived ≤ parent: a fact inferred FROM another can't be used more confidently than
 * its source. Clamps the derived use down to the parent's when a parent is known.
 */
export function clampToParent(
  use: ConversationUse,
  parentUse: ConversationUse | null | undefined,
): ConversationUse {
  if (!parentUse) return use;
  return USE_RANK[use] <= USE_RANK[parentUse] ? use : parentUse;
}

/**
 * True when two ISO timestamps fall on the same UTC calendar day. Normalizes to real
 * UTC first — a string-prefix compare would treat "2026-06-20T23:30:00-02:00" and
 * "2026-06-21T00:30:00Z" as different days though both are 2026-06-21 UTC, letting a
 * same-session acquiescence be double-counted (Codex cross-model review).
 */
export function sameUtcDay(aISO: string | null | undefined, bISO: string | null | undefined): boolean {
  if (!aISO || !bISO) return false;
  const a = Date.parse(aISO);
  const b = Date.parse(bISO);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return false;
  return new Date(a).toISOString().slice(0, 10) === new Date(b).toISOString().slice(0, 10);
}
